"use strict";

import _defineProperty from "@babel/runtime-corejs3/helpers/defineProperty";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/web.dom-collections.iterator.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useEffect, useMemo, useRef, useState } from 'react';
import { BehaviorSubject, NEVER, from, merge, of } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { useSubscription } from 'use-subscription';
import { toCamelCaseKeys } from '../helpers/toCasedKeys';
const headers = {
  'content-type': 'application/json'
};
export const userCoordsOptionCode = 'USER_COORDS';
var AbortSignals = /*#__PURE__*/function (AbortSignals) {
  AbortSignals[AbortSignals["geolocation"] = 0] = "geolocation";
  AbortSignals[AbortSignals["queryLookup"] = 1] = "queryLookup";
  AbortSignals[AbortSignals["all"] = 2] = "all";
  return AbortSignals;
}(AbortSignals || {});
const handleSuggestedSuburbsResponse = () =>
// @ts-ignore
input$ => {
  // @ts-ignore
  return input$.pipe(switchMap(response => {
    if (!response.ok) {
      return of({
        error: true,
        message: response.status
      });
    }
    return from(response.json()).pipe(map(results => toCamelCaseKeys(results)));
  }));
};
const lookupSuburb = (query, apiUrl) => fromFetch(`${apiUrl}/location?query=${query}`, {
  headers,
  method: 'GET'
}).pipe(handleSuggestedSuburbsResponse(), map(results => (results || {}).suggestions));
const lookupNearbySuburb = (lat, long, apiUrl) => fromFetch(`${apiUrl}/location/${lat}/${long}/nearby`, {
  headers,
  method: 'GET'
}).pipe(handleSuggestedSuburbsResponse(), map(results => {
  const locations = (results || {}).locations;
  if (!Array.isArray(locations) || !locations[0]) return null;
  return [_objectSpread(_objectSpread({}, locations[0]), {}, {
    value: 'Your location',
    code: userCoordsOptionCode
  })];
}));
const getCurrentPosition = () => {
  const subject = new BehaviorSubject(null);
  navigator.geolocation.getCurrentPosition(position => {
    subject.next(position);
    subject.complete();
  }, error => {
    subject.error(error);
  }, {
    enableHighAccuracy: false
  });
  return subject.asObservable();
};
export const useSuburbLookup = ({
  query,
  setValue,
  apiUrl
}) => {
  const querySubjectRef = useRef(new BehaviorSubject(query));
  const geolocateSubjectRef = useRef(new BehaviorSubject(false));
  const suggestionsSubjectRef = useRef(new BehaviorSubject([]));
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [coords, setCoords] = useState();
  useEffect(() => {
    const abortSignalSubject = new BehaviorSubject(void 0);
    const abortSignal$ = abortSignalSubject.asObservable();
    const sharedCatchPipe = catchError(() => {
      setIsLoading(false);
      setIsError(true);
      return NEVER;
    });
    const queryLookupResults$ = querySubjectRef.current.pipe(filter(query => typeof query === 'string' && query.length > 0), debounceTime(400), tap(() => {
      setIsLoading(true);
      geolocateSubjectRef.current.next(false);
      abortSignalSubject.next(AbortSignals.geolocation); // Cancel any ongoing geolocation lookup before
      // starting query lookup
    }), switchMap(currentQuery => lookupSuburb(currentQuery, apiUrl).pipe(takeUntil(abortSignal$.pipe(filter(signal => signal === AbortSignals.queryLookup))))), sharedCatchPipe);
    const locationLookupResults$ = geolocateSubjectRef.current.pipe(distinctUntilChanged(),
    // Prevent multiple clicks from making individual calls
    filter(Boolean), tap(() => {
      setIsLoading(true);
      abortSignalSubject.next(AbortSignals.queryLookup); // Cancel any ongoing query lookup before starting
      // geolocation
    }), switchMap(() => getCurrentPosition().pipe(filter(position => Boolean(position) && Boolean(position.coords)), tap(position => setCoords(position.coords)), switchMap(position => lookupNearbySuburb(position.coords.latitude, position.coords.longitude, apiUrl).pipe(filter(nearbySuburbs => nearbySuburbs && nearbySuburbs.length > 0), tap(nearbySuburbs => {
      if (typeof setValue === 'function') setValue(nearbySuburbs[0]);
    }), takeUntil(abortSignal$.pipe(filter(signal => signal === AbortSignals.geolocation))))))), sharedCatchPipe);
    merge(queryLookupResults$, locationLookupResults$).pipe(takeUntil(abortSignal$.pipe(filter(signal => signal === AbortSignals.all)))).subscribe(result => {
      if (result !== null) {
        setIsLoading(false);
        suggestionsSubjectRef.current.next(result);
      }
    });
    return () => {
      abortSignalSubject.next(AbortSignals.all);
    };
  }, [querySubjectRef]);
  const subscription = useMemo(() => ({
    getCurrentValue: () => suggestionsSubjectRef.current.getValue(),
    subscribe: callback => {
      const subscription = suggestionsSubjectRef.current.subscribe(callback);
      return () => subscription.unsubscribe();
    }
  }),
  // Re-subscribe any time the behaviorSubject changes
  [suggestionsSubjectRef]);
  return {
    setQuery: value => querySubjectRef.current.next(value),
    lookupByLocation: () => geolocateSubjectRef.current.next(true),
    suggestions: useSubscription(subscription),
    isLoading,
    isError,
    coords
  };
};